import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapPin, Briefcase, Search, Star, Zap } from 'lucide-react';
import { Job } from '../types/Job';
import { getJobs } from '../utils/storage';

const Jobs: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [typeFilter, setTypeFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState(searchParams.get('country') || '');

  useEffect(() => {
    window.scrollTo(0, 0);
    const loadJobs = async () => {
      const loadedJobs = await getJobs();
      const activeJobs = loadedJobs.filter(job => job.isActive);
      setJobs(activeJobs);
      setFilteredJobs(activeJobs);
    };
    loadJobs();
  }, []);

  useEffect(() => {
    let filtered = jobs;

    if (searchTerm) {
      filtered = filtered.filter(job =>
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (typeFilter) {
      filtered = filtered.filter(job => job.type === typeFilter);
    }

    if (countryFilter) {
      filtered = filtered.filter(job => job.country === countryFilter);
    }

    setFilteredJobs(filtered);
  }, [searchTerm, typeFilter, countryFilter, jobs]);

  const uniqueCountries = Array.from(new Set(jobs.map(job => job.country)));

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'full-time': 'Tiempo Completo',
      'part-time': 'Tiempo Parcial',
      'contract': 'Contrato',
      'temporary': 'Temporal'
    };
    return labels[type] || type;
  };

  return (
    <div>
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1920&q=80')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary-dark/90 via-primary/85 to-accent/90"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10 text-white">
          <h1 className="text-6xl font-extrabold mb-4 drop-shadow-2xl">¡Encuentra Tu Trabajo Ideal! 🌟</h1>
          <p className="text-2xl font-light drop-shadow-lg">Tu próxima gran aventura está aquí</p>
        </div>
      </section>

      <section className="py-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-primary/20">
            <div className="grid md:grid-cols-4 gap-4 items-center">
              <div className="md:col-span-2">
                <div className="relative h-full">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar por título, empresa o ubicación... 🔍"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full h-full pl-10 pr-4 py-3 border-2 border-primary/20 rounded-xl focus:ring-2 focus:ring-accent focus:border-accent transition-all"
                  />
                </div>
              </div>
              <div className="h-full">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full h-full px-4 py-3 border-2 border-primary/20 rounded-xl focus:ring-2 focus:ring-accent focus:border-accent transition-all"
                >
                  <option value="">Todos los tipos</option>
                  <option value="full-time">Tiempo Completo</option>
                  <option value="part-time">Tiempo Parcial</option>
                  <option value="contract">Contrato</option>
                  <option value="temporary">Temporal</option>
                </select>
              </div>
              <div className="h-full">
                <select
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  className="w-full h-full px-4 py-3 border-2 border-primary/20 rounded-xl focus:ring-2 focus:ring-accent focus:border-accent transition-all"
                >
                  <option value="">Todos los países</option>
                  {uniqueCountries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          {filteredJobs.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
              <p className="text-gray-600 text-2xl mb-6">
                😔 No hay ofertas disponibles ahora mismo
              </p>
              <Link
                to="/admin"
                className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg hover:scale-105 transition-all shadow-lg"
              >
                Añadir Primera Oferta
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8 text-center">
                <p className="text-2xl font-bold text-gray-800">
                  ✨ {filteredJobs.length} Oportunidad{filteredJobs.length !== 1 ? 'es' : ''} Increíble{filteredJobs.length !== 1 ? 's' : ''} ✨
                </p>
              </div>
              <div className="grid gap-6">
                {filteredJobs.map(job => (
                  <div key={job.id} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 border-l-4 border-accent">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                      <div className="flex-1">
                        <div className="flex items-start gap-2 mb-2">
                          <Star className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" fill="currentColor" />
                          <h3 className="text-2xl font-bold text-gray-800">
                            <Link to={`/job/${job.slug || job.id}`} className="hover:text-pink-600 transition">
                              {job.title}
                            </Link>
                          </h3>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                          <span className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {job.location}, {job.country}
                          </span>
                          <span className="flex items-center">
                            <Briefcase className="w-4 h-4 mr-1" />
                            {getTypeLabel(job.type)}
                          </span>
                          {job.salary && (
                            <span className="flex items-center">
                              €{job.salary}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 line-clamp-2">{job.description}</p>
                      </div>
                      <div className="mt-4 md:mt-0 md:ml-6">
                        <Link
                          to={`/job/${job.slug || job.id}`}
                          className="inline-flex items-center gap-2 bg-accent hover:bg-accent-dark text-white px-6 py-3 rounded-full font-bold hover:scale-105 transition-all shadow-lg"
                        >
                          <Zap className="w-5 h-5" />
                          Ver Detalles
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Jobs;